import { sprig, SprigAPI } from '@sprig-technologies/sprig-browser';
import { IEmployerSponsoredClaim } from 'hooks/useInfiniteQueryEmployerSponsoredClaims';
import { IStripeCard } from 'hooks/useQueryStripeCards';
import dayjs from 'utils/date-helper';

const sprigEnvId = process.env.REACT_APP_SPRIG_KEY || '';

export enum SprigEvent {
  ONBOARDING_COMPLETED = 'Onboarding completed',
  CLAIM_SUBMITTED = 'Claim submitted',
  STORE_PURCHASE_COMPLETED = 'Store purchase completed',
  SUBSCRIPTION_PURCHASE_COMPLETED = 'Subscription purchase completed',
  CARD_PURCHASE_COMPLETED = 'Card purchase completed',
  PHYSICAL_CARD_ORDERED = 'Physical card ordered',
  BACKUP_PAYMENT_METHOD_ADDED = 'Backup payment method added',
  BANK_ACCOUNT_ADDED = 'Bank account added',
}

type SprigEventMeta = {
  properties?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
};

export default class Sprig {
  private static sprigApi: SprigAPI | null = null;

  constructor() {
    Sprig.initializeSprig(sprigEnvId);
  }

  public static initializeSprig = (envId: string) => {
    if (envId) {
      Sprig.sprigApi = sprig.configure({
        environmentId: sprigEnvId || '',
      });
    }
  };

  public static identifyUser = ({
    userId,
    userEmail,
  }: {
    userId: string;
    userEmail: string;
  }) => {
    if (Sprig.sprigApi) {
      Sprig.sprigApi.setUserId(userId);
      Sprig.sprigApi.setEmail(userEmail);
    }
  };

  public static addAttributes(attributes: { [key: string]: unknown }) {
    if (Sprig.sprigApi) {
      Sprig.sprigApi.setAttributes(attributes);
    }
  }

  public static removeAttributes(keys: Array<string>) {
    if (Sprig.sprigApi) {
      Sprig.sprigApi.removeAttributes(keys);
    }
  }

  public static logoutUser = () => {
    Sprig.sprigApi && Sprig.sprigApi.logoutUser();
  };

  public static trackEvent = async (
    event: SprigEvent,
    { properties, metadata }: SprigEventMeta = {}
  ) => {
    Sprig.sprigApi && (await Sprig.sprigApi.track(event, properties, metadata));
  };
}

type SprigUserAttributeArgs = {
  country: string;
  companyName: string;
  hasStoreEnabledWallet: boolean;
  onboardedAt?: string;
};

export const recordSprigUserAttributes = ({
  country,
  companyName,
  hasStoreEnabledWallet,
  onboardedAt,
}: SprigUserAttributeArgs) => {
  const formattedActivatedDate = onboardedAt
    ? dayjs(onboardedAt).format('MM-DD-YYYY')
    : null;

  const thirtyDaysSinceOnboarding =
    Boolean(onboardedAt) && dayjs().diff(onboardedAt, 'days') >= 30;

  Sprig.addAttributes({
    Country: country,
    Company: companyName,
    'Onboarded date': formattedActivatedDate,
    /**
     * This needs to be evaluated here since Sprig does not support date comparisons
     * currently. See: https://docs.sprig.com/docs/triggers-filters-and-groups#attributes-as-filters
     */
    '30 days since onboarded date': thirtyDaysSinceOnboarding,
    'Has store-enabled wallet': hasStoreEnabledWallet,
  });
};

export type SprigPretaxAttributes = {
  isPretaxEnabled?: boolean;
  hasPretaxWallets?: boolean;
};

export const recordSprigPretaxAttributes = ({
  isPretaxEnabled,
  hasPretaxWallets,
}: SprigPretaxAttributes) => {
  const sanitizedAttrs: {
    'Pretax enabled'?: boolean;
    'Has pretax wallets'?: boolean;
  } = {};

  if (isPretaxEnabled !== undefined) {
    sanitizedAttrs['Pretax enabled'] = isPretaxEnabled;
  }

  if (hasPretaxWallets !== undefined) {
    sanitizedAttrs['Has pretax wallets'] = hasPretaxWallets;
  }

  if (Object.keys(sanitizedAttrs).length > 0) {
    Sprig.addAttributes(sanitizedAttrs);
  }
};

export const recordSprigPosttaxCardAttributes = (cards: Array<IStripeCard>) =>
  Sprig.addAttributes({
    'Has post-tax card': Array.isArray(cards) && cards.length > 0,
  });

export const recordSprigClaimsAttributes = (
  claims: Array<IEmployerSponsoredClaim>
) =>
  Sprig.addAttributes({
    'Has claims': Array.isArray(claims) && claims.length > 0,
  });

export const triggerSprigCodeEvent = async (
  event: SprigEvent,
  meta?: SprigEventMeta
) => await Sprig.trackEvent(event, meta);
